import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import { IssueRelationModal } from './IssueRelationModal';
import {
  Link2,
  Plus,
  X,
  Ban,
  Copy,
  Pencil,
  AlertTriangle,
} from 'lucide-react';
import clsx from 'clsx';

interface IssueRelationsProps {
  issueId: string;
  projectId: string;
  onNavigateToIssue?: (issueId: string) => void;
}

const RELATION_TYPE_CONFIG: Record<string, { label: string; description: string; icon: any; color: string; bgColor: string }> = {
  blocking: {
    label: 'Blocking',
    description: 'This task is blocking',
    icon: Ban,
    color: 'text-red-600',
    bgColor: 'bg-red-50 border-red-200'
  },
  blocked_by: {
    label: 'Blocked by',
    description: 'This task is blocked by',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 border-orange-200'
  },
  relates_to: {
    label: 'Related',
    description: 'This task relates to',
    icon: Link2,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-200'
  },
  duplicate_of: {
    label: 'Duplicate',
    description: 'This task is a duplicate of',
    icon: Copy,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50 border-purple-200'
  },
};

export function IssueRelations({ issueId, projectId, onNavigateToIssue }: IssueRelationsProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: relations, isLoading } = useQuery({
    queryKey: ['relations', issueId],
    queryFn: () => api.getRelations(issueId),
    enabled: !!issueId,
  });

  // Debug logging
  console.log('[IssueRelations] issueId:', issueId, 'relations:', relations, 'isLoading:', isLoading);

  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createRelation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relations', issueId] });
      setIsModalOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (relationId: string) => api.deleteRelation(relationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relations', issueId] });
    },
  });

  const handleAddRelation = (relationType: string, relatedIssueId: string) => {
    createMutation.mutate({
      bh_issue: issueId,
      bh_related_issue: relatedIssueId,
      bh_relation_type: relationType,
      bh_project: projectId,
    });
  };

  const getWorkItemById = (id: string) => {
    return workItems?.find((wi: any) => wi.bh_workitemid === id);
  };

  const getStateById = (stateId: string) => {
    return states?.find((s: any) => s.bh_stateid === stateId);
  };

  // Group relations by type
  const groupedRelations = (relations || []).reduce((acc: any, relation: any) => {
    const type = relation.bh_relation_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(relation);
    return acc;
  }, {});

  const hasRelations = relations && relations.length > 0;

  // Check for blockers specifically
  const blockers = groupedRelations['blocked_by'] || [];
  const hasBlockers = blockers.length > 0;

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      {/* Header with Add/Edit button */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Relations & Blockers</span>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className={clsx(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium transition-colors",
            hasRelations
              ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
              : "bg-primary-50 text-primary-600 hover:bg-primary-100"
          )}
        >
          {hasRelations ? (
            <>
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5" />
              Add
            </>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-sm text-gray-400 py-2">Loading...</div>
        ) : !hasRelations ? (
          <div className="py-4 text-center text-sm text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            No relations or blockers defined
          </div>
        ) : (
          <>
            {/* Show blockers prominently at the top */}
            {hasBlockers && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-semibold text-orange-800">
                    Blocked by {blockers.length} task{blockers.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-2">
                  {blockers.map((relation: any) => {
                    const relatedItem = getWorkItemById(relation.bh_related_issue);
                    if (!relatedItem) return null;
                    const state = getStateById(relatedItem._bh_state_value);

                    return (
                      <div
                        key={relation.bh_relationid}
                        className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-orange-100 group"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: state?.bh_color || '#6b7280' }}
                        />
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => onNavigateToIssue?.(relatedItem.bh_workitemid)}
                            className="text-left text-sm text-gray-800 hover:text-primary-600 font-medium truncate block w-full"
                          >
                            <span className="text-orange-600 font-mono text-xs mr-1.5">
                              #{relatedItem.bh_sequenceid}
                            </span>
                            {relatedItem.bh_name}
                          </button>
                          <span className="text-xs text-gray-500">
                            Status: {state?.bh_name || 'Unknown'}
                          </span>
                        </div>
                        <button
                          onClick={() => deleteMutation.mutate(relation.bh_relationid)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                          title="Remove blocker"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other relations */}
            {Object.entries(groupedRelations)
              .filter(([type]) => type !== 'blocked_by')
              .map(([type, typeRelations]: [string, any]) => {
                const config = RELATION_TYPE_CONFIG[type] || RELATION_TYPE_CONFIG.relates_to;
                const Icon = config.icon;

                return (
                  <div key={type} className={clsx('rounded-lg p-3 border', config.bgColor)}>
                    <div className={clsx('flex items-center gap-2 mb-2', config.color)}>
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-semibold">
                        {config.description}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {typeRelations.map((relation: any) => {
                        const relatedItem = getWorkItemById(relation.bh_related_issue);
                        if (!relatedItem) return null;
                        const state = getStateById(relatedItem._bh_state_value);

                        return (
                          <div
                            key={relation.bh_relationid}
                            className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100 group"
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: state?.bh_color || '#6b7280' }}
                            />
                            <div className="flex-1 min-w-0">
                              <button
                                onClick={() => onNavigateToIssue?.(relatedItem.bh_workitemid)}
                                className="text-left text-sm text-gray-800 hover:text-primary-600 font-medium truncate block w-full"
                              >
                                <span className={clsx('font-mono text-xs mr-1.5', config.color)}>
                                  #{relatedItem.bh_sequenceid}
                                </span>
                                {relatedItem.bh_name}
                              </button>
                              <span className="text-xs text-gray-500">
                                Status: {state?.bh_name || 'Unknown'}
                              </span>
                            </div>
                            <button
                              onClick={() => deleteMutation.mutate(relation.bh_relationid)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                              title="Remove relation"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <IssueRelationModal
          issueId={issueId}
          projectId={projectId}
          existingRelations={relations || []}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleAddRelation}
          onDelete={(relationId) => deleteMutation.mutate(relationId)}
        />
      )}
    </div>
  );
}
